import { normalizeLocale, type SupportedLocale } from '@/i18n/config';
import { getDownloadsPageCopy } from '@/i18n/downloads-page-copy';

type DownloadManagerLabels = {
  title: string;
  minimize: string;
  expand: string;
  close: string;
  viewAll: string;
  download: string;
  retry: string;
  queued: string;
  running: string;
  ready: string;
  failed: string;
  expired: string;
  cancelled: string;
  files: string;
  itemLabel: string;
  noFilesInFolder: string;
  itemCount: (count: number) => string;
  fileCount: (count: number) => string;
  fileProgress: (completed: number, total: number) => string;
  itemProgress: (name: string, progress: number) => string;
};

const EN_COPY: DownloadManagerLabels = {
  title: 'Downloads',
  minimize: 'Minimize downloads',
  expand: 'Expand downloads',
  close: 'Close downloads',
  viewAll: 'View all downloads',
  download: 'Download',
  retry: 'Retry',
  queued: 'Queued',
  running: 'Preparing',
  ready: 'Ready',
  failed: 'Preparation failed',
  expired: 'Download expired',
  cancelled: 'Cancelled',
  files: 'files',
  itemLabel: 'items',
  noFilesInFolder: 'No files in this folder to download.',
  itemCount: (count) => `${count} ${count === 1 ? 'item' : 'items'}`,
  fileCount: (count) => `${count} ${count === 1 ? 'file' : 'files'}`,
  fileProgress: (completed, total) =>
    `${completed}/${total} ${total === 1 ? 'file' : 'files'}`,
  itemProgress: (name, progress) => `${name}: ${progress}% complete`,
};

type StaticDownloadManagerLabels = Omit<
  DownloadManagerLabels,
  'retry' | 'itemCount' | 'fileCount' | 'fileProgress' | 'itemProgress'
>;

const STATIC_COPY: Record<SupportedLocale, StaticDownloadManagerLabels> = {
  en: EN_COPY,
  'pt-BR': {
    title: 'Transfer\u00EAncias',
    minimize: 'Minimizar downloads',
    expand: 'Expandir downloads',
    close: 'Fechar downloads',
    viewAll: 'Veja todos os downloads.',
    download: 'Baixe',
    queued: 'Em fila.',
    running: 'Preparando',
    ready: 'Pronto.',
    failed: 'A prepara\u00E7\u00E3o falhou.',
    expired: 'Baixar expirado',
    cancelled: 'Cancelado',
    files: 'Arquivos',
    itemLabel: 'Itens',
    noFilesInFolder: 'Nenhum arquivo nesta pasta para baixar.',
  },
  ar: {
    title: 'التنزيلات',
    minimize: 'تصغير التنزيلات',
    expand: 'توسيع التنزيلات',
    close: 'إغلاق التنزيلات',
    viewAll: 'عرض كل التنزيلات',
    download: 'تنزيل',
    queued: 'في الانتظار',
    running: 'جارٍ التجهيز',
    ready: 'جاهز',
    failed: 'فشل التجهيز',
    expired: 'انتهت صلاحية التنزيل',
    cancelled: 'تم الإلغاء',
    files: 'ملفات',
    itemLabel: 'عناصر',
    noFilesInFolder: 'لا توجد ملفات في هذا المجلد لتنزيلها.',
  },
  de: {
    title: 'Downloads',
    minimize: 'Downloads minimieren',
    expand: 'Downloads erweitern',
    close: 'Downloads schließen',
    viewAll: 'Alle Downloads anzeigen',
    download: 'Herunterladen',
    queued: 'In Warteschlange',
    running: 'Wird vorbereitet',
    ready: 'Bereit',
    failed: 'Vorbereitung fehlgeschlagen',
    expired: 'Download abgelaufen',
    cancelled: 'Abgebrochen',
    files: 'Dateien',
    itemLabel: 'Elemente',
    noFilesInFolder: 'Dieser Ordner enthält keine Dateien zum Herunterladen.',
  },
  es: {
    title: 'Descargas',
    minimize: 'Minimizar descargas',
    expand: 'Expandir descargas',
    close: 'Cerrar descargas',
    viewAll: 'Ver todas las descargas',
    download: 'Descargar',
    queued: 'En cola',
    running: 'Preparando',
    ready: 'Listo',
    failed: 'La preparación falló',
    expired: 'La descarga venció',
    cancelled: 'Cancelado',
    files: 'archivos',
    itemLabel: 'elementos',
    noFilesInFolder: 'No hay archivos en esta carpeta para descargar.',
  },
  fr: {
    title: 'Téléchargements',
    minimize: 'Réduire les téléchargements',
    expand: 'Développer les téléchargements',
    close: 'Fermer les téléchargements',
    viewAll: 'Voir tous les téléchargements',
    download: 'Télécharger',
    queued: 'En attente',
    running: 'Préparation',
    ready: 'Prêt',
    failed: 'Échec de la préparation',
    expired: 'Téléchargement expiré',
    cancelled: 'Annulé',
    files: 'fichiers',
    itemLabel: 'éléments',
    noFilesInFolder: 'Ce dossier ne contient aucun fichier à télécharger.',
  },
  hi: {
    title: 'डाउनलोड',
    minimize: 'डाउनलोड छोटा करें',
    expand: 'डाउनलोड खोलें',
    close: 'डाउनलोड बंद करें',
    viewAll: 'सभी डाउनलोड देखें',
    download: 'डाउनलोड',
    queued: 'कतार में',
    running: 'तैयार हो रहा है',
    ready: 'तैयार',
    failed: 'तैयारी विफल',
    expired: 'डाउनलोड की समय-सीमा समाप्त',
    cancelled: 'रद्द',
    files: 'फ़ाइलें',
    itemLabel: 'आइटम',
    noFilesInFolder: 'इस फ़ोल्डर में डाउनलोड करने के लिए कोई फ़ाइल नहीं है।',
  },
  id: {
    title: 'Unduhan',
    minimize: 'Minimalkan unduhan',
    expand: 'Perluas unduhan',
    close: 'Tutup unduhan',
    viewAll: 'Lihat semua unduhan',
    download: 'Unduh',
    queued: 'Dalam antrean',
    running: 'Menyiapkan',
    ready: 'Siap',
    failed: 'Penyiapan gagal',
    expired: 'Unduhan kedaluwarsa',
    cancelled: 'Dibatalkan',
    files: 'file',
    itemLabel: 'item',
    noFilesInFolder: 'Tidak ada file di folder ini untuk diunduh.',
  },
  it: {
    title: 'Download',
    minimize: 'Riduci download',
    expand: 'Espandi download',
    close: 'Chiudi download',
    viewAll: 'Visualizza tutti i download',
    download: 'Scarica',
    queued: 'In coda',
    running: 'Preparazione',
    ready: 'Pronto',
    failed: 'Preparazione non riuscita',
    expired: 'Download scaduto',
    cancelled: 'Annullato',
    files: 'file',
    itemLabel: 'elementi',
    noFilesInFolder: 'Non ci sono file da scaricare in questa cartella.',
  },
  ja: {
    title: 'ダウンロード',
    minimize: 'ダウンロードを最小化',
    expand: 'ダウンロードを展開',
    close: 'ダウンロードを閉じる',
    viewAll: 'すべてのダウンロードを表示',
    download: 'ダウンロード',
    queued: '待機中',
    running: '準備中',
    ready: '準備完了',
    failed: '準備に失敗しました',
    expired: 'ダウンロード期限切れ',
    cancelled: 'キャンセル済み',
    files: 'ファイル',
    itemLabel: '件',
    noFilesInFolder:
      'このフォルダーにはダウンロードできるファイルがありません。',
  },
  ko: {
    title: '다운로드',
    minimize: '다운로드 최소화',
    expand: '다운로드 펼치기',
    close: '다운로드 닫기',
    viewAll: '모든 다운로드 보기',
    download: '다운로드',
    queued: '대기 중',
    running: '준비 중',
    ready: '준비됨',
    failed: '준비 실패',
    expired: '다운로드 만료',
    cancelled: '취소됨',
    files: '개 파일',
    itemLabel: '개 항목',
    noFilesInFolder: '이 폴더에는 다운로드할 파일이 없습니다.',
  },
  ru: {
    title: 'Загрузки',
    minimize: 'Свернуть загрузки',
    expand: 'Развернуть загрузки',
    close: 'Закрыть загрузки',
    viewAll: 'Просмотреть все загрузки',
    download: 'Скачать',
    queued: 'В очереди',
    running: 'Подготовка',
    ready: 'Готово',
    failed: 'Ошибка подготовки',
    expired: 'Срок загрузки истёк',
    cancelled: 'Отменено',
    files: 'файлов',
    itemLabel: 'элементов',
    noFilesInFolder: 'В этой папке нет файлов для скачивания.',
  },
  vi: {
    title: 'Tải xuống',
    minimize: 'Thu nhỏ tải xuống',
    expand: 'Mở rộng tải xuống',
    close: 'Đóng tải xuống',
    viewAll: 'Xem tất cả lượt tải xuống',
    download: 'Tải xuống',
    queued: 'Đang chờ',
    running: 'Đang chuẩn bị',
    ready: 'Sẵn sàng',
    failed: 'Chuẩn bị thất bại',
    expired: 'Bản tải xuống đã hết hạn',
    cancelled: 'Đã hủy',
    files: 'tệp',
    itemLabel: 'mục',
    noFilesInFolder: 'Không có tệp nào trong thư mục này để tải xuống.',
  },
  zh: {
    title: '下载',
    minimize: '最小化下载',
    expand: '展开下载',
    close: '关闭下载',
    viewAll: '查看所有下载',
    download: '下载',
    queued: '排队中',
    running: '正在准备',
    ready: '就绪',
    failed: '准备失败',
    expired: '下载已过期',
    cancelled: '已取消',
    files: '个文件',
    itemLabel: '项',
    noFilesInFolder: '此文件夹中没有可下载的文件。',
  },
  'zh-TW': {
    title: '下載',
    minimize: '最小化下載',
    expand: '展開下載',
    close: '關閉下載',
    viewAll: '檢視所有下載',
    download: '下載',
    queued: '排隊中',
    running: '正在準備',
    ready: '就緒',
    failed: '準備失敗',
    expired: '下載已過期',
    cancelled: '已取消',
    files: '個檔案',
    itemLabel: '個項目',
    noFilesInFolder: '此資料夾中沒有可下載的檔案。',
  },
};

export type DownloadManagerCopy = DownloadManagerLabels;

export function getDownloadManagerCopy(
  locale?: string | null
): DownloadManagerCopy {
  const normalizedLocale = normalizeLocale(locale);
  const staticCopy = STATIC_COPY[normalizedLocale];
  return {
    ...staticCopy,
    retry: getDownloadsPageCopy(normalizedLocale).retry,
    itemCount: (count) =>
      normalizedLocale === 'en'
        ? EN_COPY.itemCount(count)
        : `${count} ${staticCopy.itemLabel}`,
    fileCount: (count) =>
      normalizedLocale === 'en'
        ? EN_COPY.fileCount(count)
        : `${count} ${staticCopy.files}`,
    fileProgress: (completed, total) =>
      normalizedLocale === 'en'
        ? EN_COPY.fileProgress(completed, total)
        : `${completed}/${total} ${staticCopy.files}`,
    itemProgress: (name, progress) =>
      normalizedLocale === 'en'
        ? EN_COPY.itemProgress(name, progress)
        : `${name}: ${progress}%`,
  };
}
